import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import type { PaletteMode } from '@mui/material';
import { createAppTheme } from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeModeContext } from './contexts/ThemeModeContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Collections from './components/collections';
import Orders from './components/Orders';
import SalesTracking from './components/SalesTracking';
import AdminPanel from './components/AdminPanel';
import AuditLogs from './components/AuditLogs';
import FinanceDashboard from './components/FinanceDashboard';
import FinancePage from './components/finance/FinancePage';
import MorososPage from './components/MorososPage';
import ReturnsPage from './components/ReturnsPage';
import CreditosPage from './components/CreditosPage';
import Layout from './components/Layout';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  forbiddenRoles?: string[];
}

function ProtectedRoute({ children, requiredRole, forbiddenRoles }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Cargando...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/dashboard" />;
  }

  if (forbiddenRoles?.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }
  
  return <>{children}</>;
}

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="collections" element={<Collections />} />
        <Route
          path="sales-tracking"
          element={
            <ProtectedRoute forbiddenRoles={["cobrador"]}>
              <SalesTracking />
            </ProtectedRoute>
          }
        />
        <Route path="orders" element={<Orders />} />
        <Route path="morosos" element={
          <ProtectedRoute requiredRole="admin">
            <MorososPage />
          </ProtectedRoute>
        } />
        <Route path="returns" element={
          <ProtectedRoute requiredRole="admin">
            <ReturnsPage />
          </ProtectedRoute>
        } />
        <Route path="admin" element={
          <ProtectedRoute requiredRole="admin">
            <AdminPanel />
          </ProtectedRoute>
        } />
        <Route path="audit-logs" element={
          <ProtectedRoute requiredRole="admin">
            <AuditLogs />
          </ProtectedRoute>
        } />
        <Route path="finance" element={
          <ProtectedRoute requiredRole="admin">
            <FinancePage />
          </ProtectedRoute>
        } />
        <Route path="finance-old" element={
          <ProtectedRoute requiredRole="admin">
            <FinanceDashboard />
          </ProtectedRoute>
        } />
        <Route path="creditos" element={
          <ProtectedRoute requiredRole="admin">
            <CreditosPage />
          </ProtectedRoute>
        } />
      </Route>
    </Routes>
  );
}

const THEME_STORAGE_KEY = 'app-theme-mode';

const getInitialMode = (): PaletteMode => {
  if (typeof window === 'undefined') {
    return 'light';
  }
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
};

function App() {
  const [mode, setMode] = useState<PaletteMode>(getInitialMode);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(THEME_STORAGE_KEY, mode);
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', mode === 'dark' ? '#161b2e' : '#ffffff');
    }
  }, [mode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  return (
    <ThemeModeContext.Provider value={{ mode, toggleMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export default App;
